import { useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listSpecialities,
  listRelations,
  listVisitTypes,
  listDoctors,
  inviteDoctor,
  listShifts,
  listNotifications,
  markAllNotificationsRead,
} from "api/reference";
import { visitTypeFromJson } from "schemas/visit_type";
import { doctorFromJson } from "schemas/doctor";
import { queryKeys } from "./keys";
import { CACHE } from "config/cachePolicy";
import { assertFetchV3Success } from "api/client";

const mapRelations = (data) =>
  (data || []).map((i) => ({
    value: i?.relation,
    label: i.relation
      ? `${i.relation[0].toUpperCase()}${i.relation.slice(1)}`
      : "",
  }));

export function useSpecialities(options = {}) {
  return useQuery({
    queryKey: queryKeys.specialities.all,
    queryFn: async () => {
      const response = await listSpecialities();
      const payload = response?.data;
      return Array.isArray(payload) ? payload : payload?.results || [];
    },
    staleTime: CACHE.REFERENCE,
    ...options,
  });
}

export function useRelations(options = {}) {
  return useQuery({
    queryKey: queryKeys.relations.all,
    queryFn: async () => {
      const response = await listRelations({ useLoading: false });
      const ok = assertFetchV3Success(response);
      return mapRelations(ok.data);
    },
    staleTime: CACHE.REFERENCE,
    ...options,
  });
}

export function useVisitTypes(options = {}) {
  return useQuery({
    queryKey: queryKeys.visitTypes.all,
    queryFn: async () => {
      const response = await listVisitTypes();
      const ok = assertFetchV3Success(response);
      return (ok.data || []).map(visitTypeFromJson);
    },
    staleTime: CACHE.REFERENCE,
    ...options,
  });
}

export async function fetchDoctorsPage({
  filters = {},
  data = { results: [] },
  limit = 30,
} = {}) {
  const response = await listDoctors(filters, { limit });
  return {
    ...(data || { results: [] }),
    ...response.data,
    results: [
      ...(data?.results || []),
      ...(response.data.results || []).map(doctorFromJson),
    ],
  };
}

export function useDoctorsList(filters = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.doctors.list(filters),
    queryFn: () => fetchDoctorsPage({ filters }),
    staleTime: CACHE.LIVE,
    ...options,
  });
}

export function useDoctorsPicker(filters = {}, options = {}) {
  const queryClient = useQueryClient();
  const loadMoreInFlight = useRef(false);
  const query = useDoctorsList(filters, options);

  const loadMore = async () => {
    const page = query.data;
    if (!page?.next || query.isFetching || loadMoreInFlight.current) return;
    loadMoreInFlight.current = true;
    try {
      const next = await fetchDoctorsPage({
        filters: { ...filters, offset: page.results.length },
        data: page,
      });
      queryClient.setQueryData(queryKeys.doctors.list(filters), next);
    } finally {
      loadMoreInFlight.current = false;
    }
  };

  return { ...query, loadMore };
}

export function useInviteDoctor() {
  return useMutation({
    mutationFn: ({ doctorUuid }) => inviteDoctor(doctorUuid),
  });
}

export function useShifts(filters, options = {}) {
  return useQuery({
    queryKey: queryKeys.shifts.list(filters),
    queryFn: async () => {
      const response = await listShifts(filters);
      return response;
    },
    enabled: filters != null,
    staleTime: CACHE.LIVE,
    ...options,
  });
}

export function useNotifications(options = {}) {
  return useQuery({
    queryKey: queryKeys.notifications.all(),
    queryFn: async () => {
      const response = await listNotifications();
      const ok = assertFetchV3Success(response);
      return ok.data;
    },
    staleTime: CACHE.LIVE,
    ...options,
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all() });
    },
  });
}
